<template>
  <div
    class="datepicker"
    :class="{ 'datepicker--align-right': alignRight }"
    v-click-outside="blur"
  >
    <slot name="input" :visibility-toggler="toggleVisible"></slot>
    <div
      ref="list"
      class="datepicker__content"
      v-show="isShow"
      :class="{
        datepicker__content_range: isRange,
        'datepicker__content--top': listTop
      }"
    >
      <div class="datepicker__content-header">
        <DatePickerHeader
          class="datepicker__content-header-months"
          title="Месяц"
          v-model="availableMonths"
        />
        <span class="datepicker__content-header-year">{{
          changedYearSelected || yearSelected
        }}</span>
      </div>

      <table class="datepicker__content-calendar">
        <tr>
          <th v-for="w in weekdays" :key="w">{{ w }}</th>
        </tr>

        <tr v-for="(row, i) in calendar" :key="i">
          <td
            v-for="(col, j) in row"
            :key="i + j"
            :class="[
              {
                _out: col.isOut,
                _active: col.isActive,
                _disabled: col.isDisabled || disabled
              },
              colorDays(col)
            ]"
            @click="selectDate(col.date)"
          >
            {{ col.date.format("D") }}
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import moment from "moment";

import "moment/locale/ru";
import { DatePickerHeader } from "./DatePickerHeader";
import { i18n } from "@v2/shared/config";

moment.locale(i18n.locale);

function getSelectedValueOfDropdown(options) {
  const option = options.find(option => option.selected);

  if (!option) {
    return null;
  }

  return option.value;
}

export default {
  name: "DatePicker",
  components: { DatePickerHeader },
  props: {
    preheader: {
      type: String,
      default: ""
    },
    title: {
      type: String,
      default: "Выберите"
    },
    isRange: {
      type: Boolean,
      default: false
    },
    alignRight: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    singleDatePicker: {
      type: Boolean,
      default: false
    },
    minDate: [String, Date], // строка формата 2000-10-20 (ISO 8601) или дата
    maxDate: [String, Date],
    // массив из промежутков дат, доступных для выбора, элементы массива объекты с полями startDate и endDate
    // доступность считается включая startDate и endDate
    availablePeriods: {
      type: Array,
      default: () => []
    },
    disabledDays: {
      type: Array,
      default: () => []
    },
    value: [Array, Object]
  },

  data() {
    return {
      listTop: false,
      isShow: false,
      dates: this.$props.value,
      changedYearSelected: "",
      weekdays: (() => {
        let wd = moment.weekdaysShort();
        wd.push(wd.shift());

        return wd;
      })(),

      months: (() => {
        return moment.months().map((m, index) => ({
          label: m.charAt(0).toUpperCase() + m.substr(1),
          value: index
        }));
      })(),

      years: (() => {
        const current = moment(this.minDate || undefined).year(),
          years = [];

        for (let y = current + 1; y >= 2000; y--) {
          years.push({
            label: y,
            selected: current === y,
            value: y
          });
        }

        return years;
      })()
    };
  },

  computed: {
    monthSelected() {
      return getSelectedValueOfDropdown(this.months);
    },

    yearSelected() {
      return this.years.find(
        year =>
          year.value === this.availableMonths.find(item => item.selected).year
      ).value;
    },

    startMonth() {
      return moment.min(
        this.availablePeriods
          .filter(item => item.startDate)
          .map(item => moment(item.startDate))
      );
    },

    endMonth() {
      return moment.max(
        this.availablePeriods
          ?.filter(item => item.endDate)
          ?.map(item => moment(item.endDate))
      );
    },

    availableMonths: {
      get() {
        if (this.endMonth.year() > this.startMonth.year()) {
          const prevYearMonths = this.months
            .map(month => {
              return {
                ...month,
                year: this.startMonth.year()
              };
            })
            .filter(month => month.value >= this.startMonth.month());
          const nextYearMonth = this.months
            .map(month => {
              return {
                ...month,
                year: this.endMonth.year()
              };
            })
            .filter(month => month.value <= this.endMonth.month());
          return [...prevYearMonths, ...nextYearMonth];
        } else {
          return this.months
            .map(month => {
              return {
                ...month,
                year: this.startMonth.year()
              };
            })
            .filter(
              item =>
                item.value >= this.startMonth.month() &&
                item.value <= this.endMonth.month()
            );
        }
      },
      set(value) {
        this.months = value;
        this.changedYearSelected = value.find(item => item.selected).year;
      }
    },

    calendar() {
      const calendar = [],
        d = moment()
          .year(this.changedYearSelected || this.yearSelected)
          .month(this.monthSelected)
          .date(1);

      const startDay = d
        .clone()
        .startOf("month")
        .startOf("week");

      const endDay = d
        .clone()
        .endOf("month")
        .endOf("week");

      let date = startDay.clone().subtract(1, "day");

      while (date.isBefore(endDay, "day")) {
        calendar.push(
          Array(7)
            .fill(0)
            .map(() => {
              const calendarDate = date.add(1, "day").clone();

              return {
                date: calendarDate,
                isOut: calendarDate.month() !== d.month(),

                isActive: this.singleDatePicker
                  ? calendarDate.isSame(this.value)
                  : this.dates.find(date => calendarDate.isSame(date)),

                isDisabled: this.isDateDisabled(date)
              };
            })
        );
      }
      return calendar;
    }
  },

  watch: {
    value: {
      deep: true,
      handler(val) {
        this.dates = val;
      }
    }
  },

  methods: {
    colorDays(date) {
      const momentDates = this.availablePeriods.map(item => {
        item.startDate = moment(item.startDate);
        item.endDate = moment(item.endDate);
        return item;
      });
      let daysClass = "";
      momentDates.forEach(item => {
        if (
          item.type === "installation" &&
          date.date.isBetween(item.startDate, item.endDate, "day", "[]")
        ) {
          daysClass = "datepicker__content-calendar__installation";
        }
        if (
          (item.type === "exhibition" || item.type === "ordered") &&
          date.date.isBetween(item.startDate, item.endDate, "day", "[]")
        ) {
          daysClass = "datepicker__content-calendar__exhibition";
        }
        if (
          item.type === "dismantling" &&
          date.date.isBetween(item.startDate, item.endDate, "day", "[]")
        ) {
          daysClass = "datepicker__content-calendar__dismantling";
        }
      });
      return daysClass;
    },

    selectDate(date) {
      if (this.singleDatePicker) {
        if (!this.dates || !this.dates.isSame(date)) {
          this.dates = date;
          this.blur();
        } else {
          this.dates = null;
        }
      } else {
        if (!this.dates.find(item => item === date.format("YYYY-MM-DD"))) {
          this.dates.push(date.format("YYYY-MM-DD"));
        } else {
          this.dates = this.dates.filter(
            item => item !== date.format("YYYY-MM-DD")
          );
        }
      }
      this.$emit("input", this.dates);
    },

    toggleVisible(visibility = !this.isShow) {
      this.isShow = visibility;

      this.$nextTick(() => {
        if (this.$refs.list) {
          const height = this.$refs.list.clientHeight;
          const top = this.$refs.list.getBoundingClientRect().top;
          if (top + height > window.innerHeight) {
            this.listTop = true;
            return;
          }
          this.listTop = false;
        }
      });
    },

    blur() {
      this.toggleVisible(false);
    },

    isDateDisabled(date) {
      let isDisabled = false;
      if (this.minDate) {
        let min = moment(this.minDate);
        isDisabled = date.diff(min, "days") < 0;
      }
      if (this.maxDate) {
        let max = moment(this.maxDate);
        isDisabled = isDisabled || max.diff(date, "days") < 0;
      }
      if (this.availablePeriods?.length) {
        isDisabled = !this.availablePeriods.some(period =>
          moment(date).isBetween(period.startDate, period.endDate, "day", "[]")
        );
      }
      if (this.disabledDays?.length) {
        isDisabled = this.disabledDays.some(day => date.isSame(day, "day"));
      }
      return isDisabled;
    },

    initMonths() {
      this.months = this.months.map(month => {
        return { ...month, selected: month.value === this.startMonth.month() };
      });
    }
  },

  created() {
    this.initMonths();
  }
};
</script>

<style lang="scss" scoped>
@import "@v2/app/styles/media";
.single-datepicker {
  position: relative;
  & td {
    &._active,
    &:hover {
      background: #009ee3;
      color: #fff;
    }
  }
  &__header {
    position: relative;
    &-calendar,
    &-delete {
      background: none;
      border: none;
      outline: none;

      padding: 0;

      position: absolute;

      cursor: pointer;

      .svg-icon {
        width: 16px;
        height: 16px;
        fill: #a3a8b3;
      }

      &:hover .svg-icon,
      &:focus .svg-icon {
        fill: #0070d2;
      }
    }

    &-calendar {
      right: 15px;
      bottom: 14px;

      &_hightlight {
        fill: #0070d2;
      }
    }

    &-delete {
      right: 35px;
      bottom: 13px;
    }

    &-input {
      height: 48px;
      border-radius: 2px;
      background-color: #ffffff;
      box-sizing: border-box;
      border: 1px solid #cdced0;

      @include respond-down(md) {
        height: 40px;
        padding-left: 12px;
        padding-right: 12px;
      }

      width: 100%;

      outline: none;

      padding-left: 16px;
      padding-right: 16px;

      &::placeholder {
        color: #cdced0;
      }

      &:hover,
      &:focus {
        border-color: #a3a8b3;
      }
    }
  }
}

.datepicker {
  height: 40px;

  outline: none;

  position: relative;

  &--align-right &__content {
    @media (max-width: 740px) {
      left: auto;
      right: 0;
    }
  }

  &__header {
    position: relative;

    &-calendar,
    &-delete {
      background: none;
      border: none;
      outline: none;

      padding: 0;

      position: absolute;

      cursor: pointer;

      .svg-icon {
        width: 16px;
        height: 16px;
        fill: #a3a8b3;
      }

      &:hover .svg-icon {
        fill: #0070d2;
      }
    }

    &-calendar {
      right: 15px;
      bottom: 14px;

      @include respond-down(md) {
        bottom: 10px;
      }

      &_hightlight {
        fill: #0070d2;
      }
    }

    &-delete {
      right: 35px;
      bottom: 13px;

      @include respond-down(md) {
        bottom: 10px;
      }
    }
  }

  &__content {
    background-color: #ffffff;
    width: 360px;
    min-height: 300px;
    box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.1);
    border-radius: 2px;
    position: absolute;
    right: 0;
    bottom: 0;
    transform: translateY(100%);
    z-index: 10;

    &--top {
      transform: translateY(-100%);
      top: 0;
      bottom: auto;
    }

    @include respond-down(md) {
      width: 288px;
      left: 1px;
    }
    box-sizing: border-box;

    padding: 24px 30px;

    &-header {
      display: flex;
      justify-content: space-between;

      &-months {
        width: 160px;
      }
      &-years {
        width: 100px;
      }
      &-year {
        font-size: 14px;
        font-weight: 500;
        line-height: 1.5;
        color: #6d7179;
        align-self: center;
      }
    }

    &-calendar {
      width: 100%;
      margin-top: 10px;
      &__installation,
      &__dismantling {
        background-color: #c8e2ed;
      }
      &__exhibition {
        background-color: #87ceeb;
      }

      th {
        background: var(--gray-30, #ebecf0);

        width: 40px;
        height: 32px;

        color: #6d7179;

        font-weight: normal;
        text-align: center;

        pointer-events: none;
      }

      td {
        width: 40px;
        height: 40px;

        text-align: center;

        color: #505050;
        font-weight: 500;

        cursor: pointer;

        &._out {
          color: #cdced0;
        }

        &._disabled {
          background: var(--gray-2, #eaeaea);
          pointer-events: none;
        }

        &._between {
          background: rgba(#009ee3, 0.8);
          color: #fff;
        }

        &._active,
        &:hover {
          background: #009ee3;
          color: #fff;
        }
      }
    }
  }
}
</style>
